import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Twitch, 
  Instagram, 
  MessageCircle, 
  Heart, 
  Play, 
  Eye, 
  Clock, 
  Send, 
  ExternalLink,
  ChevronDown,
  Music,
  Users,
  Disc3,
  Youtube
} from "lucide-react";

// TikTok icon component (not in lucide)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { ScrollArea } from "./components/ui/scroll-area";
import { Skeleton } from "./components/ui/skeleton";
import { Avatar, AvatarImage, AvatarFallback } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== COMPONENTS ==============

// Live Status Indicator
const LiveStatusIndicator = ({ isLive, className = "" }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div 
      className={`w-3 h-3 rounded-full ${isLive ? 'bg-[#FF003C] live-indicator' : 'bg-gray-500'}`}
    />
    <span 
      className={`font-mono text-sm font-bold tracking-wider ${isLive ? 'text-[#FF003C]' : 'text-gray-500'}`}
    >
      {isLive ? 'ON AIR' : 'OFFLINE'}
    </span>
  </div>
);

// Hero Section
const HeroSection = ({ profile, liveStatus, onWatchClick }) => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: liveStatus?.is_live ? 'none' : `url(https://customer-assets.emergentagent.com/job_633158ee-4b47-40f2-9973-1594ea991004/artifacts/t3whzw27_nikushdk%20rev-02%20%281%29.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: liveStatus?.is_live ? '#050505' : '#050505'
      }}
    >
      {/* Dark overlay */}
      {!liveStatus?.is_live && <div className="absolute inset-0 bg-black/80" />}
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-6">
        {liveStatus?.is_live ? (
          // LIVE VIEW - Show embedded stream
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            {/* Live Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16 border-2 border-[#FF003C]">
                  <AvatarImage 
                    src={profile?.profile_image_url} 
                    alt={profile?.display_name} 
                  />
                  <AvatarFallback className="bg-[#0A0A0A] text-[#A970FF]">NK</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl md:text-3xl font-mono font-bold text-white">
                      {profile?.display_name || "NikushDK"}
                    </h1>
                    <Badge className="bg-[#FF003C] text-white animate-pulse">
                      🔴 LIVE
                    </Badge>
                  </div>
                  <p className="text-gray-400">{liveStatus?.title}</p>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-mono font-bold text-[#FF003C]">
                    {liveStatus?.viewer_count?.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">WATCHING</p>
                </div>
                <Badge className="bg-[#A970FF]/20 text-[#A970FF] border border-[#A970FF]/30 px-4 py-2">
                  {liveStatus?.game_name || "Just Chatting"}
                </Badge>
              </div>
            </div>
            
            {/* Twitch Embed Player */}
            <div className="relative rounded-xl overflow-hidden neon-border" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={`https://player.twitch.tv/?channel=nikushdk&parent=${window.location.hostname}&muted=false&autoplay=true`}
                frameBorder="0"
                allowFullScreen
                scrolling="no"
                className="absolute top-0 left-0 w-full h-full"
                title="NikushDK Live Stream"
              />
            </div>
            
            {/* Live Actions */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Button
                data-testid="watch-fullscreen-btn"
                onClick={onWatchClick}
                className="bg-[#A970FF] hover:bg-[#9060E8] text-black font-bold px-6 py-5"
              >
                <Twitch className="mr-2 h-5 w-5" />
                Open in Twitch
              </Button>
              <Button
                variant="outline"
                className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 px-6 py-5"
                onClick={() => {
                  navigator.clipboard.writeText("https://twitch.tv/NikushDK");
                  toast.success("Stream link copied!");
                }}
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Share Stream
              </Button>
            </div>
          </motion.div>
        ) : (
          // OFFLINE VIEW - Show profile
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Live Status */}
            <div className="flex justify-center mb-6">
              <LiveStatusIndicator isLive={liveStatus?.is_live} />
            </div>
            
            {/* Avatar */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-8"
            >
              <Avatar className="w-32 h-32 mx-auto border-4 border-[#A970FF] neon-border">
                <AvatarImage 
                  src={profile?.profile_image_url || "https://api.dicebear.com/7.x/avataaars/svg?seed=NikushDK"} 
                  alt={profile?.display_name || "NikushDK"} 
                />
                <AvatarFallback className="bg-[#0A0A0A] text-[#A970FF] text-2xl font-mono">
                  NK
                </AvatarFallback>
              </Avatar>
            </motion.div>
            
            {/* Title */}
            <h1 className="text-5xl md:text-7xl font-mono font-black mb-4 gradient-text">
              {profile?.display_name || "NikushDK"}
            </h1>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto font-light">
              {profile?.description || "Welcome to my streaming community! Join the adventure."}
            </p>
            
            {/* Stats */}
            <div className="flex justify-center gap-8 mb-10">
              <div className="text-center">
                <p className="text-3xl font-mono font-bold text-[#A970FF]">
                  {profile?.follower_count?.toLocaleString() || "0"}
                </p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Followers</p>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                data-testid="watch-stream-btn"
                onClick={onWatchClick}
                className="bg-[#A970FF] hover:bg-[#9060E8] text-black font-bold px-8 py-6 text-lg btn-glow"
              >
                <Twitch className="mr-2 h-5 w-5" />
                Visit Channel
              </Button>
              <a
                href="https://www.twitch.tv/subs/NikushDK"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  data-testid="subscribe-btn"
                  className="bg-[#FF003C] hover:bg-[#E00036] text-white font-bold px-8 py-6 text-lg btn-glow"
                >
                  <Heart className="mr-2 h-5 w-5" />
                  Subscribe
                </Button>
              </a>
              <Button
                data-testid="join-discord-btn"
                variant="outline"
                className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 px-8 py-6 text-lg"
                onClick={() => toast.info("Discord link coming soon!")}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Join Discord
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="h-8 w-8 text-gray-500 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Clips Gallery
const ClipsGallery = ({ clips, loading }) => {
  if (loading) {
    return (
      <section data-testid="clips-section" className="py-20 px-6 bg-[#050505]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Top Clips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-video rounded-lg bg-[#0A0A0A]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="clips-section" className="py-20 px-6 bg-[#050505]">
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Top Clips</h2>
          <Badge variant="outline" className="border-[#A970FF]/30 text-[#A970FF]">
            {clips.length} clips
          </Badge>
        </div>
        
        {clips.length === 0 ? (
          <div className="text-center py-12">
            <Play className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">No clips available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clips.map((clip, index) => (
              <motion.a
                key={clip.id}
                href={clip.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                data-testid={`clip-card-${index}`}
                className="group relative block"
              >
                <Card className="overflow-hidden bg-[#0A0A0A] border-white/5 card-hover">
                  <div className="relative aspect-video">
                    <img
                      src={clip.thumbnail_url}
                      alt={clip.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Play className="h-16 w-16 text-white" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-mono">
                      {Math.floor(clip.duration)}s
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm line-clamp-2 mb-2 group-hover:text-[#A970FF] transition-colors">
                      {clip.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {clip.view_count.toLocaleString()}
                      </span>
                      <span>{clip.creator_name}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Latest VODs Section
const LatestVODs = ({ vods, loading }) => {
  if (loading) {
    return (
      <section data-testid="vods-section" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Latest VODs</h2>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg bg-[#050505]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!vods || vods.length === 0) {
    return (
      <section data-testid="vods-section" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Latest VODs</h2>
          <div className="text-center py-12">
            <Play className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">No VODs available yet</p>
          </div>
        </div>
      </section>
    );
  }

  // Format duration (e.g., "3h2m10s" to "3:02:10")
  const formatDuration = (duration) => {
    const match = duration.match(/(\d+)h?(\d+)?m?(\d+)?s?/);
    if (!match) return duration;
    
    const hours = match[1] || "0";
    const minutes = match[2] || "0";
    const seconds = match[3] || "0";
    
    if (parseInt(hours) > 0) {
      return `${hours}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
    }
    return `${minutes}:${seconds.padStart(2, "0")}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <section data-testid="vods-section" className="py-20 px-6 bg-[#0A0A0A]">
      <div className="container mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Play className="h-8 w-8 text-[#A970FF]" />
          <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Latest VODs</h2>
        </div>
        
        <div className="space-y-4">
          {vods.map((vod, index) => (
            <motion.a
              key={vod.id}
              href={vod.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              data-testid={`vod-card-${index}`}
              className="group block"
            >
              <Card className="overflow-hidden bg-[#050505] border-white/5 card-hover">
                <div className="flex flex-col md:flex-row">
                  {/* Thumbnail */}
                  <div className="relative w-full md:w-64 lg:w-80 flex-shrink-0">
                    <div className="aspect-video">
                      <img
                        src={vod.thumbnail_url || "https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg"}
                        alt={vod.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-mono">
                      {formatDuration(vod.duration)}
                    </div>
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Play className="h-12 w-12 text-white" />
                    </div>
                  </div>
                  
                  {/* Info */}
                  <CardContent className="p-4 flex-1">
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[#A970FF] transition-colors line-clamp-2">
                      {vod.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {vod.view_count.toLocaleString()} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDate(vod.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.a>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <a
            href="https://www.twitch.tv/nikushdk/videos"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="border-[#A970FF]/30 text-[#A970FF] hover:bg-[#A970FF]/10">
              View All VODs
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Top Clip Creators Section
const TopClipCreators = ({ creators, loading }) => {
  const medals = ["🥇", "🥈", "🥉", "4.", "5."];
  
  if (loading) {
    return (
      <section data-testid="top-creators-section" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Top Clip Creators</h2>
          <div className="space-y-4 max-w-2xl">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg bg-[#050505]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!creators || creators.length === 0) {
    return null;
  }

  return (
    <section data-testid="top-creators-section" className="py-20 px-6 bg-[#0A0A0A]">
      <div className="container mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Users className="h-8 w-8 text-[#FFD600]" />
          <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Top Clip Creators</h2>
        </div>
        
        <div className="space-y-4 max-w-2xl">
          {creators.map((creator, index) => (
            <motion.div
              key={creator.name}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              data-testid={`creator-${index}`}
              className="glass rounded-xl p-4 flex items-center gap-4 hover:border-[#A970FF]/50 transition-colors"
            >
              {/* Rank */}
              <div className="text-2xl font-bold w-10 text-center">
                {medals[index] || `${index + 1}.`}
              </div>
              
              {/* Creator Info */}
              <div className="flex-1">
                <h3 className="font-mono font-bold text-lg text-[#A970FF]">{creator.name}</h3>
                <p className="text-sm text-gray-400">
                  Best clip: <span className="text-gray-300">{creator.best_clip}</span>
                </p>
              </div>
              
              {/* Stats */}
              <div className="text-right">
                <p className="font-mono font-bold text-xl text-[#00F0FF]">{creator.clip_count}</p>
                <p className="text-xs text-gray-500">clips</p>
              </div>
              <div className="text-right">
                <p className="font-mono font-bold text-lg text-[#00FF94]">{creator.total_views.toLocaleString()}</p>
                <p className="text-xs text-gray-500">views</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// StreamElements Leaderboard Section
const LeaderboardSection = () => {
  return (
    <section data-testid="leaderboard-section" className="py-20 px-6 bg-[#050505]">
      <div className="container mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Clock className="h-8 w-8 text-[#00F0FF]" />
          <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Top Viewers</h2>
        </div>
        
        <div className="glass rounded-xl neon-border p-8 max-w-2xl text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-2xl font-mono font-bold text-[#FFD600] mb-2">Watch Time Leaderboard</h3>
            <p className="text-gray-400">
              See who has spent the most time watching NikushDK streams!
            </p>
          </div>
          
          <div className="space-y-3">
            <a
              href="https://streamelements.com/nikushdk/leaderboard"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leaderboard-btn"
            >
              <Button className="bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold px-8 py-6 text-lg btn-glow w-full sm:w-auto">
                <Eye className="mr-2 h-5 w-5" />
                View Full Leaderboard
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <p className="text-gray-500 text-sm">
              Powered by StreamElements
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Spotify Section
const SpotifySection = ({ playlistId }) => {
  return (
    <section data-testid="spotify-section" className="py-20 px-6 bg-[#0A0A0A]">
      <div className="container mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Disc3 className="h-8 w-8 text-[#1DB954]" />
          <h2 className="text-3xl md:text-4xl font-mono font-bold">My Playlist</h2>
        </div>
        
        <div className="glass rounded-xl p-6 neon-border max-w-3xl">
          <iframe
            style={{ borderRadius: '12px' }}
            src={`https://open.spotify.com/embed/playlist/${playlistId}?utm_source=generator&theme=0`}
            width="100%"
            height="352"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify Playlist"
          />
          <div className="mt-4 flex items-center justify-between">
            <p className="text-gray-500 text-sm">Stream music from my favorite playlist</p>
            <a 
              href={`https://open.spotify.com/playlist/${playlistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1DB954] hover:underline text-sm flex items-center gap-1"
            >
              <Music className="h-4 w-4" />
              Open in Spotify
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// YouTube Shorts Section
const YouTubeShortsSection = ({ shorts, loading }) => {
  if (loading) {
    return (
      <section data-testid="youtube-shorts-section" className="py-20 px-6 bg-[#050505]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Top Shorts</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-lg bg-[#0A0A0A]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!shorts || shorts.length === 0) {
    return null;
  }

  const formatViews = (views) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  return (
    <section data-testid="youtube-shorts-section" className="py-20 px-6 bg-[#050505]">
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Youtube className="h-8 w-8 text-[#FF0000]" />
            <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Top Shorts</h2>
          </div>
          <a
            href="https://www.youtube.com/@NikushDK/shorts"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF0000] hover:underline text-sm flex items-center gap-1"
          >
            View All
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {shorts.map((short, index) => (
            <motion.a
              key={short.id}
              href={short.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              data-testid={`short-card-${index}`}
              className="group relative block"
            >
              <Card className="overflow-hidden bg-[#0A0A0A] border-white/5 card-hover">
                <div className="relative aspect-[9/16]">
                  <img
                    src={short.thumbnail_url}
                    alt={short.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Play className="h-12 w-12 text-white" />
                  </div>
                  {/* View count */}
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-semibold line-clamp-2 mb-1">{short.title}</p>
                    <div className="flex items-center gap-1 text-gray-300 text-xs">
                      <Eye className="h-3 w-3" />
                      {formatViews(short.view_count)} views
                    </div>
                  </div>
                </div>
              </Card>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

// TikTok Section
const TikTokSection = ({ tiktoks, loading }) => {
  if (loading) {
    return (
      <section data-testid="tiktok-section" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-mono font-bold mb-8 gradient-text">Top TikToks</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-lg bg-[#050505]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!tiktoks || tiktoks.length === 0) {
    return null;
  }

  return (
    <section data-testid="tiktok-section" className="py-20 px-6 bg-[#0A0A0A]">
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <TikTokIcon className="h-8 w-8 text-[#ff0050]" />
            <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Top TikToks</h2>
          </div>
          <a
            href="https://www.tiktok.com/@nikushdk.hlc"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#ff0050] hover:underline text-sm flex items-center gap-1"
          >
            View All
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {tiktoks.map((tiktok, index) => (
            <motion.a
              key={index}
              href={tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              data-testid={`tiktok-card-${index}`}
              className="group relative block"
            >
              <Card className="overflow-hidden bg-[#050505] border-white/5 card-hover">
                <div className="relative aspect-[9/16]">
                  {tiktok.thumbnail_url ? (
                    <img
                      src={tiktok.thumbnail_url}
                      alt={tiktok.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#ff0050]/20 via-[#00f2ea]/20 to-[#ff0050]/20 flex items-center justify-center">
                      <TikTokIcon className="h-12 w-12 text-white/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Play className="h-12 w-12 text-white" />
                  </div>
                  {/* TikTok icon badge */}
                  <div className="absolute top-2 right-2">
                    <TikTokIcon className="h-5 w-5 text-white drop-shadow-lg" />
                  </div>
                  {/* Title */}
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-semibold line-clamp-2">{tiktok.title}</p>
                  </div>
                </div>
              </Card>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Chat Widget
const ChatWidget = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [username, setUsername] = useState("");
  const [isJoined, setIsJoined] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const colors = ["#A970FF", "#00F0FF", "#FF003C", "#00FF94", "#FFD600"];

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages`);
      setMessages(response.data);
    } catch (e) {
      console.error("Failed to fetch messages:", e);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleJoin = () => {
    if (username.trim()) {
      setIsJoined(true);
      toast.success(`Welcome, ${username}!`);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !isJoined) return;

    setLoading(true);
    try {
      const color = colors[Math.floor(Math.random() * colors.length)];
      await axios.post(`${API}/chat/messages`, {
        username,
        message: newMessage,
        color
      });
      setNewMessage("");
      await fetchMessages();
    } catch (e) {
      toast.error("Failed to send message");
    }
    setLoading(false);
  };

  return (
    <section data-testid="chat-section" className="py-20 px-6 bg-[#050505]">
      <div className="container mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Users className="h-8 w-8 text-[#A970FF]" />
          <h2 className="text-3xl md:text-4xl font-mono font-bold gradient-text">Community Chat</h2>
        </div>
        
        <Card className="glass border-white/10 max-w-2xl overflow-hidden">
          <CardContent className="p-0">
            {/* Messages Area */}
            <ScrollArea className="h-80 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>No messages yet. Be the first to say hi!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg, index) => (
                    <motion.div
                      key={msg.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex gap-2"
                    >
                      <span 
                        className="font-bold font-mono text-sm"
                        style={{ color: msg.color }}
                      >
                        {msg.username}:
                      </span>
                      <span className="text-gray-300 text-sm">{msg.message}</span>
                    </motion.div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <Separator className="bg-white/10" />
            
            {/* Input Area */}
            <div className="p-4">
              {!isJoined ? (
                <div className="flex gap-2">
                  <Input
                    data-testid="chat-username-input"
                    placeholder="Enter your name..."
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleJoin()}
                    className="bg-[#0A0A0A] border-white/10 focus:border-[#A970FF]"
                  />
                  <Button
                    data-testid="chat-join-btn"
                    onClick={handleJoin}
                    className="bg-[#A970FF] hover:bg-[#9060E8] text-black"
                  >
                    Join
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="chat-message-input"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="bg-[#0A0A0A] border-white/10 focus:border-[#A970FF]"
                    disabled={loading}
                  />
                  <Button
                    data-testid="chat-send-btn"
                    onClick={handleSendMessage}
                    disabled={loading || !newMessage.trim()}
                    className="bg-[#A970FF] hover:bg-[#9060E8] text-black"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer-section" className="py-16 px-6 bg-[#0A0A0A] border-t border-white/10">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Brand */}
          <div className="text-center md:text-left">
            <h3 className="text-2xl font-mono font-bold gradient-text mb-2">NikushDK</h3>
            <p className="text-gray-500 text-sm">Streaming community since 2024</p>
          </div>
          
          {/* Social Links */}
          <div className="flex gap-4">
            <a
              href="https://twitch.tv/NikushDK"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-twitch"
              className="p-3 rounded-lg bg-[#050505] border border-white/10 hover:border-[#A970FF] hover:text-[#A970FF] transition-all"
            >
              <Twitch className="h-5 w-5" />
            </a>
            <a
              href="https://www.youtube.com/@NikushDK"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-youtube"
              className="p-3 rounded-lg bg-[#050505] border border-white/10 hover:border-[#FF0000] hover:text-[#FF0000] transition-all"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a
              href="https://www.tiktok.com/@nikushdk.hlc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-tiktok"
              className="p-3 rounded-lg bg-[#050505] border border-white/10 hover:border-[#ff0050] hover:text-[#ff0050] transition-all"
            >
              <TikTokIcon className="h-5 w-5" />
            </a>
            <a
              href="https://instagram.com/NikushDK"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              className="p-3 rounded-lg bg-[#050505] border border-white/10 hover:border-[#E4405F] hover:text-[#E4405F] transition-all"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>
          
          {/* Donation */}
          <div>
            <a
              href="https://streamelements.com/nikushdk/tip"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                data-testid="donate-btn"
                className="bg-[#FF003C] hover:bg-[#E00036] text-white font-bold btn-glow"
              >
                <Heart className="mr-2 h-4 w-4" />
                Support Me
              </Button>
            </a>
          </div>
        </div>
        
        <Separator className="my-8 bg-white/10" />
        
        <p className="text-center text-gray-600 text-sm">
          © 2024 NikushDK. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// ============== MAIN PAGE ==============

const HomePage = () => {
  const [profile, setProfile] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [clips, setClips] = useState([]);
  const [vods, setVods] = useState([]);
  const [topCreators, setTopCreators] = useState([]);
  const [youtubeShorts, setYoutubeShorts] = useState([]);
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [playlistId, setPlaylistId] = useState("37i9dQZF1DXcBWIGoYBM5M");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch profile
      try {
        const profileRes = await axios.get(`${API}/streamer/profile/NikushDK`);
        setProfile(profileRes.data);
      } catch (e) {
        console.log("Profile fetch failed, using defaults");
      }

      // Fetch live status
      try {
        const liveRes = await axios.get(`${API}/streamer/live-status/NikushDK`);
        setLiveStatus(liveRes.data);
      } catch (e) {
        console.log("Live status fetch failed");
      }

      // Fetch clips
      try {
        const clipsRes = await axios.get(`${API}/streamer/clips/NikushDK?limit=9`);
        setClips(clipsRes.data);
      } catch (e) {
        console.log("Clips fetch failed");
      }

      // Fetch VODs
      try {
        const vodsRes = await axios.get(`${API}/streamer/vods/NikushDK?limit=3`);
        setVods(vodsRes.data);
      } catch (e) {
        console.log("VODs fetch failed");
      }

      // Fetch top clip creators
      try {
        const creatorsRes = await axios.get(`${API}/streamer/top-clip-creators/NikushDK?limit=5`);
        setTopCreators(creatorsRes.data);
      } catch (e) {
        console.log("Top creators fetch failed");
      }

      // Fetch Spotify playlist ID
      try {
        const spotifyRes = await axios.get(`${API}/spotify/playlist-id`);
        setPlaylistId(spotifyRes.data.playlist_id);
      } catch (e) {
        console.log("Spotify fetch failed, using default");
      }

      // Fetch YouTube Shorts
      try {
        const shortsRes = await axios.get(`${API}/youtube/shorts?limit=6`);
        setYoutubeShorts(shortsRes.data);
      } catch (e) {
        console.log("YouTube shorts fetch failed");
      }

      // Fetch TikTok Videos
      try {
        const tiktokRes = await axios.get(`${API}/tiktok/videos`);
        setTiktokVideos(tiktokRes.data);
      } catch (e) {
        console.log("TikTok videos fetch failed");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll live status every 30 seconds
    const interval = setInterval(async () => {
      try {
        const liveRes = await axios.get(`${API}/streamer/live-status/NikushDK`);
        setLiveStatus(liveRes.data);
      } catch (e) {
        // Silently fail
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleWatchClick = () => {
    window.open("https://twitch.tv/NikushDK", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <HeroSection 
        profile={profile} 
        liveStatus={liveStatus} 
        onWatchClick={handleWatchClick}
      />
      <LatestVODs vods={vods} loading={loading} />
      <ClipsGallery clips={clips} loading={loading} />
      <TikTokSection tiktoks={tiktokVideos} loading={loading} />
      <YouTubeShortsSection shorts={youtubeShorts} loading={loading} />
      <TopClipCreators creators={topCreators} loading={loading} />
      <LeaderboardSection />
      <SpotifySection playlistId={playlistId} />
      <ChatWidget />
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
